import CustomerHome from './tabs/home/CustomerHome';
import VendorMarketplace from './tabs/vendors/VendorMarketplace';
import CustomerBankingOverview from './tabs/banking/CustomerBankingOverview';
import CustomerAssets from './tabs/assets/CustomerAssets';
import { useState, useMemo } from 'react';
import CustomerSettings from './tabs/settings/CustomerSettings';
import { Routes, Route } from 'react-router-dom';
import CustomerFinancesOverview from './tabs/finances/CustomerFinancesOverview';
import { useUser } from '../../../utils/UserContext';
import { Entity, CustomerWithDriverOnboarding } from '../../../types/apiTypes';
import DriversHome from './tabs/drivers/DriversHome';
import BrokerNetwork from './tabs/broker-network/BrokerNetwork';
import ResourcesPage from './tabs/resources/ResourcesPage';
import SubmitMileageDrivers from './tabs/assets/SubmitMileageDrivers';
import { isPreActiveCustomer } from './tabs/home/utils/demoData';
import useFrontChat from '../../../hooks/useFrontChat';
import useApi from '../../../utils/AxiosWrapper';
import { useQuery } from '@tanstack/react-query';
import { applicationQueryKey } from '../../../hooks/useOptimisticApplicationStep';
import { Application } from '../../../types/dbTypes';
import { unwrapApiResponse } from '../../../utils/unwrapApiResponse';
import Cards from './tabs/banking/cards/Cards';
import { MilesChat } from '../../miles';
import CompleteOnboardingModal from './CompleteOnboardingModal';

interface CustomerDashboardProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
  showSideNav: boolean;
  setShowSideNav: (show: boolean) => void;
}

function CustomerDashboard({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
  showSideNav,
  setShowSideNav,
}: CustomerDashboardProps) {
  const [currSubPage, setCurrSubPage] = useState<string | null>(null);
  const { userRecord, userId } = useUser();
  const { callProtectedApi } = useApi();
  const customerStatus = (userRecord as CustomerWithDriverOnboarding)?.status;
  const isPreActive = useMemo(
    () => isPreActiveCustomer(customerStatus),
    [customerStatus]
  );

  // Initialize Front Chat - only show for pre-active customers
  // Active customers use MILES AI assistant instead
  const shouldShowFrontChat =
    isPreActive && userRecord?.userType === Entity.Customer;
  useFrontChat(shouldShowFrontChat);

  // Fetch customer's application data to determine current onboarding step
  const { data: applicationData } = useQuery<Application | null>({
    queryKey: applicationQueryKey(userId),
    queryFn: async () => {
      const response = await callProtectedApi(
        `/applications/recent/${userId ?? ''}`,
        'get',
        {},
        'Error fetching application data.'
      );
      return (
        unwrapApiResponse<{ application: Application | null }>(
          response,
          'Error fetching application data.'
        ).application ?? null
      );
    },
    enabled: !!userId && userRecord?.userType === Entity.Customer,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 5 * 60 * 1000,
  });

  // The query resolves to null when the customer has no application yet;
  // consumers below treat "no application" as undefined.
  const application = applicationData ?? undefined;

  const handleSetSubPage = (page: string | null) => {
    setCurrSubPage(page);
  };

  const commonProps = {
    userType,
    currentPage,
    currentTab,
    setCurrentTab,
    subTab,
    setSubTab,
    currSubPage,
    setCurrSubPage: handleSetSubPage,
  };

  // CustomerHome-specific props that include application data
  const customerHomeProps = {
    ...commonProps,
    application,
  };

  return (
    <>
      {userRecord?.status &&
        (userRecord?.status === 'active' ||
          userRecord?.status === 'pre_active') &&
        userRecord?.userType === Entity.Customer && (
          <Routes>
            <Route path="/" element={<CustomerHome {...customerHomeProps} />} />
            <Route
              path="home"
              element={<CustomerHome {...customerHomeProps} />}
            />
            <Route
              path="banking/*"
              element={<CustomerBankingOverview {...commonProps} />}
            />
            <Route
              path="finances/*"
              element={<CustomerFinancesOverview {...commonProps} />}
            />
            <Route
              path="assets/*"
              element={<CustomerAssets {...commonProps} />}
            />
            <Route
              path="vendor-marketplace/*"
              element={<VendorMarketplace {...commonProps} />}
            />
            <Route
              path="drivers/*"
              element={<DriversHome {...commonProps} />}
            />
            {/* broker-network is active-only: its only entry point is the
                Promote My Business card on the (active-only) Resources page, so
                registering it for pre_active left a route reachable only by
                typing the URL. Gated here to match — pre_active gets neither a
                link nor the route. (2026-07-22, Scott: the entry moved to
                Resources; pre-active was never meant to reach it — CustomerHome
                already gated it !isPreActive on every branch.) */}
            {customerStatus === 'active' && (
              <Route path="broker-network" element={<BrokerNetwork />} />
            )}
            <Route path="resources" element={<ResourcesPage />} />
            <Route
              path="settings/*"
              element={<CustomerSettings {...commonProps} />}
            />
          </Routes>
        )}
      {userRecord?.status &&
        userRecord?.status === 'active' &&
        userRecord?.userType === Entity.Driver && (
          <Routes>
            <Route
              path="vendor-marketplace/*"
              element={<VendorMarketplace {...commonProps} />}
            />
            <Route path="/" element={<VendorMarketplace {...commonProps} />} />
            <Route
              path="submit-mileage-drivers"
              element={<SubmitMileageDrivers {...commonProps} />}
            />
            <Route path="cards-drivers" element={<Cards />} />
          </Routes>
        )}

      {/* MILES AI Assistant - Only show for active customers */}
      {userRecord?.status === 'active' &&
        userRecord?.userType === Entity.Customer && (
          <MilesChat customerId={userId ?? null} />
        )}

      {/* Onboarding Reminder Modal for Pre-Active Customers */}
      <CompleteOnboardingModal
        application={application}
        isPreActive={isPreActive}
      />
    </>
  );
}

export default CustomerDashboard;
